import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  DELIVERY_MODE_LABELS,
  NOTIFICATION_EVENT_TYPE_CATALOG,
  appointmentReminderPoliciesApi,
  eventTypeLabel,
  notificationDigestApi,
  notificationEventSettingsApi,
  emailTransportApi,
  type EmailTransportState,
  notificationPreferencesApi,
  notificationsApi,
  type AppointmentReminderPolicy,
  type AppointmentReminderPolicyForm,
  type DeliveryMode,
  type NotificationCategory,
  type NotificationEventSetting,
  type NotificationListFilters,
  type NotificationPreferenceRow,
  type NotificationRow,
  type ReminderRecipient,
} from '../api/notifications'
import { campaignsApi, type Campaign } from '../api/campaigns'
import { fetchMe } from '../api/auth'
import { PushDevicePanel } from '../components/PushDevicePanel'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  FormGrid,
  Button, Callout, Card, CardContent, CardHeader, Checkbox, CircularProgress, DataTable, EmptyState, FilterBar, Icon, IconButton,
  LoadingState, Page, PageHeader, Select, StatusPill, Switch, Tabs, TextField, useConfirm, type PillTone,
} from '../components/ui'

/** Il vocabolario dei tipi di appuntamento (REQ-079-001), lo stesso di `AppointmentTypes` sul server. */
const REMINDER_APPOINTMENT_TYPES = [
  'Telefonico', 'Videochiamata', 'Presso cliente', 'Presso ufficio', 'Presso negozio', 'Demo', 'Analisi fattura', 'Consulenza',
]

const PAGE_SIZE = 20

/** REQ-267-001's own four category names, verbatim — icon + Italian label + badge color, so the
 * category is never conveyed by color alone (accessibility requirement from this task's brief). */
const CATEGORY_META: Record<NotificationCategory, { label: string; icon: string; tone: PillTone; iconClass: string }> = {
  Critica: { label: 'Critica', icon: 'error', tone: 'error', iconClass: 'text-danger' },
  RichiedeAzione: { label: 'Richiede azione', icon: 'priority_high', tone: 'warning', iconClass: 'text-warning' },
  Informativa: { label: 'Informativa', icon: 'info', tone: 'info', iconClass: 'text-primary' },
  Successo: { label: 'Successo', icon: 'check_circle', tone: 'success', iconClass: 'text-success' },
}
const CATEGORY_ORDER: NotificationCategory[] = ['Critica', 'RichiedeAzione', 'Informativa', 'Successo']

/**
 * Which `referenceType` values this frontend knows how to deep-link — today only `"Contract"`
 * (verified against the one real producer, `ContractLifecycleNotificationDispatcher.cs`, which
 * always sets `ReferenceType: "Contract"`). A notification whose `referenceType` is anything else,
 * or `null`, or missing from this map, renders as plain text with no link — per this task's brief,
 * never a broken link.
 */
const REFERENCE_ROUTES: Record<string, (id: number) => string> = {
  Contract: id => `/contracts/${id}`,
  // F-078: le comunicazioni sul lotto di scarico (invio per approvazione, scarico approvato).
  Batch: id => `/batches?id=${id}`,
}

function referencePath(row: NotificationRow): string | null {
  if (!row.referenceType || row.referenceId === null) return null
  const build = REFERENCE_ROUTES[row.referenceType]
  return build ? build(row.referenceId) : null
}

function formatRelative(iso: string): string {
  const diffMs = Math.max(0, Date.now() - new Date(iso).getTime())
  const sec = Math.floor(diffMs / 1000)
  if (sec < 60) return 'pochi istanti fa'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} ${min === 1 ? 'minuto' : 'minuti'} fa`
  const hour = Math.floor(min / 60)
  if (hour < 24) return `${hour} ${hour === 1 ? 'ora' : 'ore'} fa`
  const day = Math.floor(hour / 24)
  if (day < 30) return `${day} ${day === 1 ? 'giorno' : 'giorni'} fa`
  const month = Math.floor(day / 30)
  if (month < 12) return `${month} ${month === 1 ? 'mese' : 'mesi'} fa`
  const year = Math.floor(month / 12)
  return `${year} ${year === 1 ? 'anno' : 'anni'} fa`
}

function formatAbsolute(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Errore'
}

export function NotificationsPage() {
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<'list' | 'preferences' | 'channels' | 'reminders'>(
    () => searchParams.get('tab') === 'preferences' ? 'preferences' : 'list')
  // Le due schede Admin compaiono solo per gli amministratori (la scrittura e' PolicyNames.Admin sul server).
  const [isAdmin, setIsAdmin] = useState(false)
  useEffect(() => {
    fetchMe()
      .then(me => setIsAdmin(me !== null && ['Administrator', 'EqualAdmin'].includes(me.role)))
      .catch(() => setIsAdmin(false))
  }, [])

  return (
    <Page>
      <PageHeader
        title="Notifiche"
        icon="notifications"
        tabs={
          <Tabs
            aria-label="Sezioni del centro notifiche"
            value={tab}
            onChange={id => setTab(id as typeof tab)}
            items={[
              { id: 'list', label: 'Notifiche' },
              { id: 'preferences', label: 'Preferenze' },
              ...(isAdmin
                ? [
                    { id: 'channels', label: 'Canali per evento' },
                    { id: 'reminders', label: 'Promemoria appuntamenti' },
                  ]
                : []),
            ]}
          />
        }
      />

      {tab === 'list' && <NotificationsList />}
      {tab === 'preferences' && <PreferencesPanel />}
      {tab === 'channels' && isAdmin && <EventChannelsPanel />}
      {tab === 'reminders' && isAdmin && <AppointmentRemindersPanel />}
    </Page>
  )
}

function NotificationsList() {
  const navigate = useNavigate()
  const confirm = useConfirm()

  const [items, setItems] = useState<NotificationRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [category, setCategory] = useState<NotificationCategory | ''>('')
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [includeArchived, setIncludeArchived] = useState(false)
  // F-087: quando valorizzato, l'elenco mostra solo le notifiche raccolte da quel riepilogo.
  const [digestId, setDigestId] = useState<number | null>(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [unreadTotal, setUnreadTotal] = useState<number | null>(null)
  const [markingAll, setMarkingAll] = useState(false)
  // Per-row in-flight guard, keyed by notification id — disables that row's own buttons only,
  // rather than freezing the whole list while e.g. one archive call is pending.
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set())

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const refreshUnreadTotal = useCallback(() => {
    notificationsApi.unreadCount().then(r => setUnreadTotal(r.count)).catch(() => { /* header count only — see NotificationBell for the same tolerant pattern */ })
  }, [])

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    const params: NotificationListFilters = { unreadOnly, includeArchived, page, size: PAGE_SIZE }
    if (category) params.category = category
    if (digestId) params.digestId = digestId
    notificationsApi.list(params)
      .then(result => { setItems(result.items); setTotal(result.total) })
      .catch(e => setError(errorMessage(e)))
      .finally(() => setLoading(false))
  }, [category, unreadOnly, includeArchived, digestId, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { refreshUnreadTotal() }, [refreshUnreadTotal])

  // Any filter change starts back at page 1 — a stale "page 3" from a previous, larger result set
  // would otherwise silently render an out-of-range empty page.
  function setCategoryFilter(v: NotificationCategory | '') { setCategory(v); setPage(1) }
  function setUnreadOnlyFilter(v: boolean) { setUnreadOnly(v); setPage(1) }
  function setIncludeArchivedFilter(v: boolean) { setIncludeArchived(v); setPage(1) }

  function withBusy(id: number, fn: () => Promise<void>) {
    setBusyIds(prev => new Set(prev).add(id))
    fn().finally(() => setBusyIds(prev => { const next = new Set(prev); next.delete(id); return next }))
  }

  /**
   * REQ-267-001 + this task's brief: reading is an explicit action, never a side effect of
   * scrolling. The two places that call this are both deliberate: the "Segna come letta" button,
   * and opening a notification's own reference (see `openReference` below) — the latter counts as
   * "apertura del dettaglio", exactly the example this task's brief itself gives.
   */
  function markRead(row: NotificationRow) {
    if (row.readAt) return
    withBusy(row.id, async () => {
      try {
        await notificationsApi.markRead(row.id)
        if (unreadOnly) {
          // The "solo non lette" filter must keep meaning what it says: a row that just became
          // read no longer belongs in this view, same "drop it locally instead of waiting for a
          // reload" idea as `archive` below dropping an archived row when archived ones are hidden.
          setItems(prev => prev.filter(i => i.id !== row.id))
          setTotal(t => Math.max(0, t - 1))
        } else {
          setItems(prev => prev.map(i => i.id === row.id ? { ...i, readAt: new Date().toISOString() } : i))
        }
        setUnreadTotal(t => (t !== null ? Math.max(0, t - 1) : t))
      } catch (e) {
        setError(errorMessage(e))
      }
    })
  }

  function openReference(row: NotificationRow, path: string) {
    markRead(row)
    navigate(path)
  }

  async function archive(row: NotificationRow) {
    const ok = await confirm({
      title: 'Archiviare la notifica',
      message: `Archiviare questa notifica? Una volta archiviata non comparirà più nell'elenco (a meno di scegliere "Mostra archiviate"), e non è possibile riportarla indietro.`,
      confirmLabel: 'Archivia',
      destructive: true,
    })
    if (!ok) return
    withBusy(row.id, async () => {
      try {
        await notificationsApi.archive(row.id)
        if (!includeArchived) {
          setItems(prev => prev.filter(i => i.id !== row.id))
          setTotal(t => Math.max(0, t - 1))
        } else {
          setItems(prev => prev.map(i => i.id === row.id ? { ...i, archivedAt: new Date().toISOString() } : i))
        }
        if (!row.readAt) setUnreadTotal(t => (t !== null ? Math.max(0, t - 1) : t))
      } catch (e) {
        setError(errorMessage(e))
      }
    })
  }

  async function markAllRead() {
    if (markingAll) return
    setMarkingAll(true)
    setError(null)
    try {
      await notificationsApi.markAllRead()
      if (unreadOnly) {
        // Marking ALL of the caller's unread notifications read (server-wide, not just this page)
        // means the "solo non lette" view has nothing left to show — same reasoning as the
        // single-item case above.
        setItems([])
        setTotal(0)
      } else {
        const now = new Date().toISOString()
        setItems(prev => prev.map(i => i.readAt ? i : { ...i, readAt: now }))
      }
      setUnreadTotal(0)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setMarkingAll(false)
    }
  }

  const filtersActive = category !== '' || unreadOnly || includeArchived

  return (
    <>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div aria-live="polite" className="text-muted md-typescale-body-small">
          {unreadTotal === null
            ? 'Verifica delle notifiche non lette…'
            : unreadTotal === 0
              ? 'Nessuna notifica non letta'
              : `${unreadTotal} ${unreadTotal === 1 ? 'notifica non letta' : 'notifiche non lette'}`}
        </div>
        <Button
          type="button"
          variant="outlined"
          size="sm"
          disabled={markingAll || !unreadTotal}
          onClick={() => { void markAllRead() }}
        >
          {markingAll ? 'Aggiornamento…' : 'Segna tutte come lette'}
        </Button>
      </div>

      <FilterBar>
        <Select
          id="notif-category-filter"
          label="Categoria"
          value={category}
          onChange={e => setCategoryFilter(e.target.value as NotificationCategory | '')}
        >
          <option value="">Tutte le categorie</option>
          {CATEGORY_ORDER.map(c => (
            <option key={c} value={c}>{CATEGORY_META[c].label}</option>
          ))}
        </Select>

        <Switch
          id="notif-unread-only"
          label="Solo non lette"
          checked={unreadOnly}
          onChange={e => setUnreadOnlyFilter(e.target.checked)}
        />

        <Switch
          id="notif-include-archived"
          label="Mostra archiviate"
          checked={includeArchived}
          onChange={e => setIncludeArchivedFilter(e.target.checked)}
        />
      </FilterBar>

      {digestId !== null && (
        <Callout
          tone="info"
          role="status"
          action={
            <Button type="button" variant="outlined" size="sm" onClick={() => { setDigestId(null); setPage(1) }}>
              Torna all&apos;elenco
            </Button>
          }
        >
          Stai vedendo le notifiche raccolte in un riepilogo.
        </Callout>
      )}

      {error && (
        <Callout
          tone="error"
          role="alert"
          action={<Button type="button" variant="text" onClick={load}>Riprova</Button>}
        >
          {error}
        </Callout>
      )}

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          !error && (
            <EmptyState
              icon="notifications_off"
              title={filtersActive
                ? 'Nessuna notifica corrisponde ai filtri selezionati.'
                : 'Nessuna notifica. Quando succede qualcosa che ti riguarda, la trovi qui.'}
            />
          )
        ) : (
          <ul className="list-unstyled mb-0">
            {items.map(row => {
              const meta = CATEGORY_META[row.category]
              const path = referencePath(row)
              const isUnread = !row.readAt
              const busy = busyIds.has(row.id)
              return (
                <li
                  key={row.id}
                  className="px-3 py-3 border-bottom"
                  style={isUnread ? { background: 'hsl(var(--md-primary) / .05)' } : undefined}
                >
                  <div className="d-flex align-items-start gap-3">
                    <Icon name={meta.icon} size={24} className={`${meta.iconClass} mt-1`} />

                    <div className="flex-grow-1 md-u-minw-0">
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                        {isUnread && <StatusPill tone="primary">Non letta</StatusPill>}
                        {row.archivedAt && <StatusPill tone="neutral">Archiviata</StatusPill>}
                      </div>

                      {path ? (
                        <Button
                          type="button"
                          variant="text"
                          size="sm"
                          onClick={() => openReference(row, path)}
                        >
                          {row.title}
                        </Button>
                      ) : (
                        <div className="md-typescale-title-small">{row.title}</div>
                      )}

                      <div className="text-muted md-typescale-body-small mb-1">{row.body}</div>

                      {row.eventType === 'notification.digest' && (
                        <Button
                          type="button"
                          variant="text"
                          size="sm"
                          onClick={() => { setDigestId(row.id); setPage(1) }}
                        >
                          Mostra le notifiche incluse
                        </Button>
                      )}

                      <div className="text-muted md-typescale-body-small">
                        <time dateTime={row.createdAt} title={formatAbsolute(row.createdAt)}>
                          {formatRelative(row.createdAt)}
                        </time>
                        {' '}
                        <span className="opacity-75">({formatAbsolute(row.createdAt)})</span>
                      </div>
                    </div>

                    <div className="d-flex flex-column flex-sm-row gap-2">
                      {isUnread && (
                        <Button
                          type="button"
                          variant="outlined"
                          size="sm"
                          disabled={busy}
                          onClick={() => markRead(row)}
                        >
                          Segna come letta
                        </Button>
                      )}
                      {!row.archivedAt && (
                        <Button
                          type="button"
                          variant="outlined"
                          size="sm"
                          disabled={busy}
                          onClick={() => { void archive(row) }}
                        >
                          Archivia
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
        {totalPages > 1 && (
          <div className="d-flex align-items-center justify-content-between p-2">
            <span className="text-muted md-typescale-body-small">Pagina {page} di {totalPages} ({total} totali)</span>
            <div className="d-flex gap-2">
              <Button type="button" variant="outlined" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prec</Button>
              <Button type="button" variant="outlined" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Succ</Button>
            </div>
          </div>
        )}
      </Card>
    </>
  )
}

const MODE_ORDER: DeliveryMode[] = ['Immediata', 'Riepilogo', 'Disattivata']

/** Lunedi' = 1 ... domenica = 7 (giorni ISO, come il backend). */
const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Lun' }, { value: 2, label: 'Mar' }, { value: 3, label: 'Mer' }, { value: 4, label: 'Gio' },
  { value: 5, label: 'Ven' }, { value: 6, label: 'Sab' }, { value: 7, label: 'Dom' },
]

/** Il valore proposto nel campo orario quando l'utente non ne ha ancora uno: e' un suggerimento, non e' attivo. */
const SUGGESTED_DIGEST_TIME = '18:00'
const SUGGESTED_REMINDER_MINUTES = 15

/** Il modo effettivo di una riga: `mode` (F-087) se c'e', altrimenti dedotto da `enabled`. */
function rowMode(r: NotificationPreferenceRow): DeliveryMode {
  return r.mode ?? (r.enabled ? 'Immediata' : 'Disattivata')
}

function DigestSchedulePanel({ onChange }: { onChange: (enabled: boolean) => void }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [isEnabled, setIsEnabled] = useState(false)
  const [localTime, setLocalTime] = useState(SUGGESTED_DIGEST_TIME)
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5])

  useEffect(() => {
    notificationDigestApi.get()
      .then(d => {
        setIsEnabled(d.isEnabled)
        if (d.localTime) setLocalTime(d.localTime)
        if (d.configured) setWeekdays(d.weekdays)
        onChange(d.isEnabled)
      })
      .catch(e => setError(errorMessage(e)))
      .finally(() => setLoading(false))
  }, [onChange])

  function toggleDay(day: number) {
    setWeekdays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => a - b))
  }

  async function save() {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      await notificationDigestApi.save({ isEnabled, localTime, weekdays })
      onChange(isEnabled)
      setSaved(true)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card variant="outlined" padding="none">
      <CardHeader title="Riepilogo delle notifiche" icon="schedule_send" />
      <CardContent>
        <p className="text-muted md-typescale-body-small">
          Il riepilogo è una sola notifica nel centro notifiche (non è un&apos;email) che raccoglie a un orario fisso
          gli eventi per cui hai scelto &laquo;Nel riepilogo&raquo;. Senza un orario attivo tutte le notifiche arrivano
          subito. Le notifiche critiche arrivano sempre subito. L&apos;orario è quello di Roma.
        </p>
        {error && <Callout tone="error" role="alert" className="mb-3">{error}</Callout>}
        {loading ? (
          <CircularProgress size="sm" />
        ) : (
          <>
            <div className="mb-3">
              <Switch
                id="digest-enabled"
                label="Ricevi il riepilogo"
                checked={isEnabled}
                onChange={e => { setIsEnabled(e.target.checked); setSaved(false) }}
              />
            </div>
            <div className="d-flex flex-wrap align-items-start gap-4 mb-3">
              <TextField
                id="digest-time"
                type="time"
                label="Orario (ora di Roma)"
                supportingText={`Suggerito: ${SUGGESTED_DIGEST_TIME}`}
                value={localTime}
                onChange={e => { setLocalTime(e.target.value); setSaved(false) }}
              />
              <fieldset>
                <legend className="md-typescale-label-large mb-1">Giorni</legend>
                <div className="d-flex flex-wrap gap-2">
                  {WEEKDAYS.map(d => (
                    <Checkbox
                      key={d.value}
                      id={`digest-day-${d.value}`}
                      label={d.label}
                      checked={weekdays.includes(d.value)}
                      onChange={() => { toggleDay(d.value); setSaved(false) }}
                    />
                  ))}
                </div>
              </fieldset>
            </div>
            <Button type="button" variant="filled" size="sm" disabled={saving} onClick={() => { void save() }}>
              {saving ? 'Salvataggio…' : 'Salva orario'}
            </Button>
            {saved && <span className="text-success md-typescale-body-small ms-3">Salvato</span>}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function PreferencesPanel() {
  const [rows, setRows] = useState<NotificationPreferenceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [savedKey, setSavedKey] = useState<string | null>(null)
  const [digestEnabled, setDigestEnabled] = useState<boolean | null>(null)
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pushOn = useFeatureFlag('notifiche.push')

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    notificationPreferencesApi.list()
      .then(setRows)
      .catch(e => setError(errorMessage(e)))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => () => { if (savedTimer.current !== null) clearTimeout(savedTimer.current) }, [])

  // Absence of a row means "subito" (backend's documented default) — the map only records explicit
  // overrides, exactly mirroring what the backend itself keeps.
  const modeByEventType = useMemo(() => {
    const map = new Map<string, DeliveryMode>()
    rows.forEach(r => { if (r.channel === 'Internal') map.set(r.eventType, rowMode(r)) })
    return map
  }, [rows])

  // Telefono: senza riga vale acceso (stessa regola del server, PushQueue).
  const pushOffByEventType = useMemo(() => {
    const off = new Set<string>()
    rows.forEach(r => { if (r.channel === 'Push' && rowMode(r) === 'Disattivata') off.add(r.eventType) })
    return off
  }, [rows])

  async function changePush(eventType: string, enabled: boolean) {
    setSavingKey(eventType)
    setError(null)
    try {
      await notificationPreferencesApi.setPush(eventType, enabled)
      setRows(prev => [
        ...prev.filter(r => !(r.eventType === eventType && r.channel === 'Push')),
        { eventType, channel: 'Push', enabled, mode: enabled ? 'Immediata' : 'Disattivata', updatedAt: new Date().toISOString() },
      ])
      if (savedTimer.current !== null) clearTimeout(savedTimer.current)
      setSavedKey(eventType)
      savedTimer.current = setTimeout(() => setSavedKey(null), 2500)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSavingKey(null)
    }
  }

  const anyDigest = useMemo(() => Array.from(modeByEventType.values()).includes('Riepilogo'), [modeByEventType])

  async function changeMode(eventType: string, mode: DeliveryMode) {
    setSavingKey(eventType)
    setError(null)
    try {
      await notificationPreferencesApi.setMode(eventType, mode)
      setRows(prev => {
        const others = prev.filter(r => !(r.eventType === eventType && r.channel === 'Internal'))
        return [...others, {
          eventType, channel: 'Internal', enabled: mode !== 'Disattivata', mode, updatedAt: new Date().toISOString(),
        }]
      })
      if (savedTimer.current !== null) clearTimeout(savedTimer.current)
      setSavedKey(eventType)
      savedTimer.current = setTimeout(() => setSavedKey(null), 2500)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSavingKey(null)
    }
  }

  const extraRows = rows.filter(
    r => r.channel === 'Internal' && !NOTIFICATION_EVENT_TYPE_CATALOG.some(e => e.eventType === r.eventType),
  )

  function renderRow(eventType: string, label: string) {
    const mode = modeByEventType.get(eventType) ?? 'Immediata'
    return (
      <li key={eventType} className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom">
        <div>
          <div className="md-typescale-emphasized-body-medium">{label}</div>
          {savedKey === eventType && <div className="text-success md-typescale-body-small">Salvato</div>}
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
          {pushOn && (
            <Switch
              id={`push-${eventType}`}
              label="Telefono"
              checked={mode !== 'Disattivata' && !pushOffByEventType.has(eventType)}
              disabled={savingKey === eventType || mode === 'Disattivata'}
              onChange={e => { void changePush(eventType, e.target.checked) }}
            />
          )}
          <Select
            label={`${label} — come ricevere la notifica`}
            labelHidden
            value={mode}
            disabled={savingKey === eventType}
            onChange={e => { void changeMode(eventType, e.target.value as DeliveryMode) }}
          >
            {MODE_ORDER.map(m => <option key={m} value={m}>{DELIVERY_MODE_LABELS[m]}</option>)}
          </Select>
        </div>
      </li>
    )
  }

  return (
    <>
      <PushDevicePanel />
      <DigestSchedulePanel onChange={setDigestEnabled} />

      <Card variant="outlined" padding="none">
        <CardHeader title="Notifiche per tipo di evento" icon="tune" />
        <CardContent>
          <p className="text-muted md-typescale-body-small">
            Per ogni evento scegli se vederlo subito, raccolto nel riepilogo oppure no. Un evento non toccato qui
            arriva subito (salvo diversa impostazione dell&apos;amministratore). Le notifiche critiche arrivano
            sempre subito. Canale: centro notifiche del CRM{pushOn ? ' e, con «Telefono» acceso, notifica sul telefono (solo per quello che arriva subito; il riepilogo arriva come una notifica sola)' : ''}. Il salvataggio è
            immediato.
          </p>

          {anyDigest && digestEnabled === false && (
            <Callout tone="warning" role="alert" className="mb-3">
              Hai scelto &laquo;Nel riepilogo&raquo; ma non hai un orario attivo: finché non ne imposti uno le notifiche
              arrivano subito.
            </Callout>
          )}

          {error && (
            <Callout
              tone="error"
              role="alert"
              className="mb-3"
              action={<Button type="button" variant="text" onClick={load}>Riprova</Button>}
            >
              {error}
            </Callout>
          )}

          {loading ? (
            <div className="text-center py-4"><CircularProgress size="sm" /></div>
          ) : (
            <ul className="list-unstyled mb-0">
              {NOTIFICATION_EVENT_TYPE_CATALOG.map(entry => renderRow(entry.eventType, entry.label))}
              {/* Preferences set by the caller for an event type this frontend's catalog doesn't
                  (yet) know about: still shown, honestly labelled with the raw technical key. */}
              {extraRows.map(r => renderRow(r.eventType, eventTypeLabel(r.eventType)))}
            </ul>
          )}
        </CardContent>
      </Card>
    </>
  )
}

/** F-087 (REQ-268-001): "Canali per evento", solo Admin. */
function EventChannelsPanel() {
  const confirm = useConfirm()
  const [items, setItems] = useState<NotificationEventSetting[]>([])
  const [emailReason, setEmailReason] = useState('')
  const [emailAvailable, setEmailAvailable] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<Record<string, { label: string; defaultMode: DeliveryMode; allowDigest: boolean; emailEnabled: boolean }>>({})
  const [smtp, setSmtp] = useState<EmailTransportState | null>(null)
  const [smtpTest, setSmtpTest] = useState<{ busy: boolean; ok?: boolean; text?: string }>({ busy: false })

  useEffect(() => { emailTransportApi.get().then(setSmtp).catch(() => setSmtp(null)) }, [])

  const testSmtp = async () => {
    setSmtpTest({ busy: true })
    try {
      const r = await emailTransportApi.test()
      setSmtpTest({ busy: false, ok: r.ok, text: r.ok ? 'Connessione riuscita.' : (r.error ?? 'Connessione non riuscita.') })
    } catch (e) {
      setSmtpTest({ busy: false, ok: false, text: errorMessage(e) })
    }
  }

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    notificationEventSettingsApi.list()
      .then(r => {
        setItems(r.items)
        setEmailAvailable(r.emailChannel.available)
        setEmailReason(r.emailChannel.available ? '' : r.emailChannel.reason)
        setDrafts({})
      })
      .catch(e => setError(errorMessage(e)))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  function draftOf(s: NotificationEventSetting) {
    return drafts[s.eventType] ?? {
      label: s.label || NOTIFICATION_EVENT_TYPE_CATALOG.find(e => e.eventType === s.eventType)?.label || '',
      defaultMode: s.defaultMode,
      allowDigest: s.allowDigest,
      emailEnabled: s.emailEnabled,
    }
  }

  function patch(s: NotificationEventSetting, change: Partial<{ label: string; defaultMode: DeliveryMode; allowDigest: boolean; emailEnabled: boolean }>) {
    setDrafts(prev => ({ ...prev, [s.eventType]: { ...draftOf(s), ...change } }))
  }

  async function save(s: NotificationEventSetting) {
    setSavingKey(s.eventType)
    setError(null)
    try {
      const d = draftOf(s)
      const { emailEnabled, ...rest } = d
      await notificationEventSettingsApi.save(s.eventType, emailAvailable ? { ...rest, emailEnabled } : rest)
      load()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSavingKey(null)
    }
  }

  async function reset(s: NotificationEventSetting) {
    const ok = await confirm({
      title: 'Ripristinare l’evento',
      message: 'Riportare questo evento alle impostazioni predefinite (subito, riepilogo ammesso)?',
      confirmLabel: 'Ripristina',
    })
    if (!ok) return
    setSavingKey(s.eventType)
    try {
      await notificationEventSettingsApi.reset(s.eventType)
      load()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <Card variant="outlined" padding="none">
      <CardHeader title="Canali per evento" icon="alt_route" />
      <CardContent>
        <p className="text-muted md-typescale-body-small">
          Per ogni tipo di evento visto nel sistema scegli l&apos;etichetta, il modo predefinito per chi non ha una
          preferenza propria e se l&apos;evento può finire nel riepilogo. Le notifiche critiche restano sempre immediate.
        </p>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <StatusPill tone="success">Centro notifiche: attivo</StatusPill>
          {emailAvailable
            ? <StatusPill tone="success">Email: disponibile</StatusPill>
            : <StatusPill tone="neutral" title={emailReason}>Email: non disponibile</StatusPill>}
        </div>
        {!emailAvailable && (
          <Callout tone="info" className="mb-3">
            {emailReason || 'SMTP non configurato: l\u2019email non è disponibile.'} Finché non si configura il server di posta
            (impostazioni SMTP) le notifiche arrivano solo nel centro notifiche del CRM e la colonna Email resta bloccata.
          </Callout>
        )}
        {smtp && (
          <div className="mb-3">
            <p className="text-muted md-typescale-body-small">
              Server di posta: {smtp.settings.configured
                ? `${smtp.settings.host}:${smtp.settings.port} (${smtp.settings.security}), mittente ${smtp.settings.fromName} <${smtp.settings.fromAddress}>.`
                : 'non configurato (le credenziali si impostano nelle variabili d\u2019ambiente del server, mai qui).'}
              {' '}Invio dalla coda: {smtp.sendingEnabled ? 'attivo' : 'spento (funzione email.smtp)'}.
            </p>
            <Button type="button" variant="outlined" size="sm" onClick={testSmtp} disabled={smtpTest.busy || !smtp.settings.configured}>
              Prova la connessione
            </Button>
            {smtpTest.text && (
              <Callout tone={smtpTest.ok ? 'success' : 'error'} role="status" className="mt-2">{smtpTest.text}</Callout>
            )}
          </div>
        )}
        {error && <Callout tone="error" role="alert" className="mb-3">{error}</Callout>}
        {loading ? (
          <div className="text-center py-4"><CircularProgress size="sm" /></div>
        ) : items.length === 0 ? (
          <div className="text-muted">Nessun evento ancora visto: comparirà qui appena arriva una notifica.</div>
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Evento</th><th>Etichetta</th><th>Modo predefinito</th><th>Riepilogo ammesso</th><th>Anche per email</th><th />
              </tr>
            </thead>
            <tbody>
              {items.map(s => {
                const d = draftOf(s)
                const busy = savingKey === s.eventType
                return (
                  <tr key={s.eventType}>
                    <td><code>{s.eventType}</code>{!s.configured && <StatusPill tone="neutral" className="ms-2">predefinito</StatusPill>}</td>
                    <td>
                      <TextField
                        maxLength={200}
                        value={d.label}
                        onChange={e => patch(s, { label: e.target.value })}
                        label={`Etichetta di ${s.eventType}`}
                        labelHidden
                      />
                    </td>
                    <td>
                      <Select
                        value={d.defaultMode}
                        onChange={e => patch(s, { defaultMode: e.target.value as DeliveryMode })}
                        label={`Modo predefinito di ${s.eventType}`}
                        labelHidden
                      >
                        {MODE_ORDER.map(m => <option key={m} value={m}>{DELIVERY_MODE_LABELS[m]}</option>)}
                      </Select>
                    </td>
                    <td>
                      <Checkbox
                        checked={d.allowDigest}
                        onChange={e => patch(s, { allowDigest: e.target.checked })}
                        label={<span className="md-sr-only">{`Riepilogo ammesso per ${s.eventType}`}</span>}
                      />
                    </td>
                    <td>
                      <Checkbox
                        checked={emailAvailable && d.emailEnabled}
                        disabled={!emailAvailable}
                        onChange={e => patch(s, { emailEnabled: e.target.checked })}
                        label={<span className="md-sr-only">{emailAvailable ? `Invia anche per email ${s.eventType}` : `Email non disponibile per ${s.eventType}: ${emailReason}`}</span>}
                      />
                    </td>
                    <td className="text-nowrap">
                      <Button type="button" variant="filled" size="sm" className="me-2" disabled={busy} onClick={() => { void save(s) }}>Salva</Button>
                      {s.configured && (
                        <Button type="button" variant="outlined" size="sm" disabled={busy} onClick={() => { void reset(s) }}>Ripristina</Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </DataTable>
        )}
      </CardContent>
    </Card>
  )
}

const RECIPIENT_LABELS: Record<ReminderRecipient, string> = {
  Assegnatario: 'Assegnatario', Creatore: 'Chi ha creato l\'appuntamento', Entrambi: 'Entrambi',
}

const EMPTY_POLICY_FORM: AppointmentReminderPolicyForm = {
  name: '', campaignId: null, appointmentType: null, minutesBefore: SUGGESTED_REMINDER_MINUTES,
  recipient: 'Assegnatario', isActive: true,
}

/** F-087 (REQ-084-002, REQ-085-001): "Promemoria appuntamenti", solo Admin. Solo notifica interna al commerciale. */
function AppointmentRemindersPanel() {
  const confirm = useConfirm()
  const [policies, setPolicies] = useState<AppointmentReminderPolicy[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<AppointmentReminderPolicyForm>(EMPTY_POLICY_FORM)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    appointmentReminderPoliciesApi.list()
      .then(r => setPolicies(r.items))
      .catch(e => setError(errorMessage(e)))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    campaignsApi.list().then(p => setCampaigns(p.items)).catch(() => { /* il filtro per campagna resta vuoto */ })
  }, [])

  const campaignName = (id: number | null) =>
    id === null ? 'Tutte' : (campaigns.find(c => c.id === id)?.name ?? `Campagna ${id}`)

  async function submit() {
    setSaving(true)
    setError(null)
    try {
      if (editingId === null) await appointmentReminderPoliciesApi.create(form)
      else await appointmentReminderPoliciesApi.update(editingId, form)
      setForm(EMPTY_POLICY_FORM)
      setEditingId(null)
      load()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  async function remove(p: AppointmentReminderPolicy) {
    const ok = await confirm({
      title: 'Eliminare la regola',
      message: `Eliminare la regola "${p.name}"?`,
      confirmLabel: 'Elimina',
      destructive: true,
    })
    if (!ok) return
    try {
      await appointmentReminderPoliciesApi.remove(p.id)
      load()
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  async function toggleActive(p: AppointmentReminderPolicy) {
    try {
      await appointmentReminderPoliciesApi.update(p.id, {
        name: p.name, campaignId: p.campaignId, appointmentType: p.appointmentType,
        minutesBefore: p.minutesBefore, recipient: p.recipient, isActive: !p.isActive,
      })
      load()
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  function edit(p: AppointmentReminderPolicy) {
    setEditingId(p.id)
    setForm({
      name: p.name, campaignId: p.campaignId, appointmentType: p.appointmentType,
      minutesBefore: p.minutesBefore, recipient: p.recipient, isActive: p.isActive,
    })
  }

  return (
    <Card variant="outlined" padding="none">
      <CardHeader title="Promemoria appuntamenti" icon="alarm" />
      <CardContent>
        <p className="text-muted md-typescale-body-small">
          Un promemoria è una notifica interna al commerciale poco prima dell&apos;appuntamento. Nessun messaggio
          parte al cliente e non c&apos;è integrazione con calendari esterni. Senza regole non viene inviato nulla;
          l&apos;invio automatico va attivato dalla configurazione del server.
        </p>
        {error && <Callout tone="error" role="alert" className="mb-3">{error}</Callout>}

        <FormGrid columns={4} className="mb-4">
          <div>
            <TextField id="rem-name" label="Nome" maxLength={150} value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Select id="rem-campaign" label="Campagna" value={form.campaignId ?? ''}
              onChange={e => setForm({ ...form, campaignId: e.target.value ? Number(e.target.value) : null })}>
              <option value="">Tutte</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
          <div>
            <Select id="rem-type" label="Tipo" value={form.appointmentType ?? ''}
              onChange={e => setForm({ ...form, appointmentType: e.target.value || null })}>
              <option value="">Tutti</option>
              {REMINDER_APPOINTMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </div>
          <div>
            <TextField id="rem-minutes" type="number" min={1} max={1440} label="Minuti prima"
              value={form.minutesBefore}
              onChange={e => setForm({ ...form, minutesBefore: Number(e.target.value) })} />
          </div>
          <div>
            <Select id="rem-recipient" label="A chi" value={form.recipient}
              onChange={e => setForm({ ...form, recipient: e.target.value as ReminderRecipient })}>
              {(Object.keys(RECIPIENT_LABELS) as ReminderRecipient[]).map(r => (
                <option key={r} value={r}>{RECIPIENT_LABELS[r]}</option>
              ))}
            </Select>
          </div>
          <div className="md-span-full d-flex gap-2">
            <Button type="button" variant="filled" size="sm" disabled={saving || !form.name.trim()} onClick={() => { void submit() }}>
              {editingId === null ? 'Aggiungi' : 'Salva'}
            </Button>
            {editingId !== null && (
              <Button type="button" variant="text" size="sm"
                onClick={() => { setEditingId(null); setForm(EMPTY_POLICY_FORM) }}>Annulla</Button>
            )}
          </div>
          <div className="md-span-full md-typescale-body-small text-muted">Suggerito: {SUGGESTED_REMINDER_MINUTES} minuti prima.</div>
        </FormGrid>

        {loading ? (
          <div className="text-center py-4"><CircularProgress size="sm" /></div>
        ) : policies.length === 0 ? (
          <div className="text-muted">Nessuna regola: nessun promemoria viene inviato.</div>
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr><th>Nome</th><th>Campagna</th><th>Tipo</th><th>Anticipo</th><th>A chi</th><th>Stato</th><th /></tr>
            </thead>
            <tbody>
              {policies.map(p => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{campaignName(p.campaignId)}</td>
                  <td>{p.appointmentType ?? 'Tutti'}</td>
                  <td>{p.minutesBefore} min</td>
                  <td>{RECIPIENT_LABELS[p.recipient]}</td>
                  <td>{p.isActive ? <StatusPill tone="success">Attiva</StatusPill> : <StatusPill tone="neutral">Sospesa</StatusPill>}</td>
                  <td className="text-nowrap">
                    <Button type="button" variant="outlined" size="sm" className="me-1" onClick={() => edit(p)}>Modifica</Button>
                    <Button type="button" variant="outlined" size="sm" className="me-1" onClick={() => { void toggleActive(p) }}>
                      {p.isActive ? 'Sospendi' : 'Riattiva'}
                    </Button>
                    <IconButton icon="delete" aria-label="Elimina" size="sm" onClick={() => { void remove(p) }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </CardContent>
    </Card>
  )
}
